import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ClipboardList, Users, FileCheck, CheckCircle2, Loader2,
  Send, FileText, ArrowRight, Search, Megaphone, BadgeCheck, Mail,
  ChevronRight, Info, Download, Hourglass, XCircle, Gavel,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { recruitmentClientApi } from '@/lib/recruitmentApi';
import { clientApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { DonutCard, ProgressCard, SEM } from '@/components/client/SuiteAnalytics';
import { ClientTalentCheckCreateDialog } from '@/components/client/ClientTalentCheckCreateDialog';
import { RequestHMSupport } from '@/components/shared/RequestHMSupport';
import { bizDate } from '@/lib/dates';
import { toast } from 'sonner';

function KpiCard({ icon: Icon, label, value, hint, accent = 'primary', cta, onCta, testid }) {
  const accents = {
    primary: 'from-primary/15 to-primary/5 text-primary',
    success: 'from-success/15 to-success/5 text-success',
    warning: 'from-warning/15 to-warning/5 text-warning',
    info: 'from-info/15 to-info/5 text-info',
  };
  return (
    <Card className="p-4 shadow-card hover:shadow-elevated transition-smooth" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${accents[accent]}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium leading-tight whitespace-normal [text-wrap:balance]">{label}</p>
          <p className="text-xl font-bold tabular-nums mt-0.5">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
          {cta && (
            <button className="text-xs text-primary hover:underline mt-1" onClick={onCta}>
              {cta} <ChevronRight className="inline h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

const STAGE_ICONS = [Send, FileText, BadgeCheck, Users, Mail, FileCheck, ClipboardList, CheckCircle2];

export default function ClientTalentCheck() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Founder UAT Wave 2 — pending-approval strip + cancellation flow.
  const [evalReqs, setEvalReqs] = useState([]);
  const [cancelReq, setCancelReq] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, r, reqs] = await Promise.all([
        recruitmentClientApi.workspace().catch(() => null),
        clientApi.listReports().catch(() => []),
        clientApi.listEvaluationRequests().catch(() => []),
      ]);
      setWorkspace(w);
      setReports(Array.isArray(r) ? r : []);
      setEvalReqs(Array.isArray(reqs) ? reqs : []);
    } catch (e) {
      toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const procs = useMemo(() => workspace?.processes || [], [workspace]);

  const handleExportPdf = useCallback(async () => {
    try {
      const res = await api.get('/client/recruitment/export/pdf', {
        responseType: 'blob', timeout: 60000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'talentcheck-processes.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    }
  }, [t]);

  // Map 11-stage backend → 8 displayed stages
  // 1→1, 2→2, 3→3, 4→4, 5→5, 6→6, 7/8/9→7, 10/11→8
  const stageBuckets = useMemo(() => {
    const arr = [0, 0, 0, 0, 0, 0, 0, 0];
    for (const p of procs) {
      const s = p.stage || 1;
      if (s >= 10) arr[7] += 1;
      else if (s >= 7) arr[6] += 1;
      else arr[s - 1] += 1;
    }
    return arr;
  }, [procs]);

  const kpis = useMemo(() => {
    if (workspace?.kpis) return workspace.kpis;
    return {
      open_processes: 0, closed_processes: 0, active_candidates: 0,
      pending_assessments: 0, reports_awaiting_review: 0,
      reports_released: 0, hiring_completed: 0,
    };
  }, [workspace]);

  const recent = useMemo(() => procs.slice(0, 6), [procs]);

  // Founder UAT Wave 2 — TalentCheck requests still awaiting HM approval.
  const pendingReqs = useMemo(
    () => evalReqs.filter((r) => r.suite_code === 'talentcheck'
      && ['pending', 'cancellation_requested'].includes(r.status)),
    [evalReqs],
  );

  const [confirmingCancel, setConfirmingCancel] = useState(null);

  const confirmLegacyCancellation = async (r) => {
    setConfirmingCancel(r.id);
    try {
      await api.post(`/client/evaluation-requests/${r.id}/confirm-cancellation`);
      toast.success(t('clientTalentCheck.pendingStrip.cancelConfirmed', { defaultValue: 'Solicitud cancelada. El registro se conserva en el historial.' }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setConfirmingCancel(null); }
  };

  const submitCancellation = async () => {
    if (!cancelReq || cancelReason.trim().length < 3) return;
    setCancelSaving(true);
    try {
      await clientApi.requestCancellation(cancelReq.id, cancelReason.trim());
      toast.success(t('pages.clientRequests.cancelSuccess', { defaultValue: 'Cancellation requested. Your advisor will review it.' }));
      setCancelReq(null);
      setCancelReason('');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setCancelSaving(false); }
  };

  if (loading) {
    return (
      <>
        <Topbar title={t('clientTalentCheck.title')} subtitle={t('clientTalentCheck.subtitle')} />
        <div className="py-16 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={(
          <span className="inline-flex items-center gap-2">
            {t('clientTalentCheck.title')}
            <Badge className="bg-primary/15 text-primary border-primary/30">
              {t('suiteChip.talentcheck')}
            </Badge>
          </span>
        )}
        subtitle={t('clientTalentCheck.subtitle')}
      />

      <div className="p-6 lg:p-8 space-y-5" data-testid="client-tcheck-page">
        {/* Info panel */}
        <Card className="p-5 shadow-card flex items-start gap-3 bg-gradient-to-br from-primary/5 to-transparent">
          <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-sm">{t('clientTalentCheck.what.title')}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t('clientTalentCheck.what.body')}</p>
          </div>
          <Button size="sm" variant="ghost" data-testid="tcheck-more">{t('clientTalentCheck.what.more')}</Button>
        </Card>

        {/* KPIs + CTA — Sprint TC-D.2 workspace KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-7 gap-3" data-testid="tcheck-kpis">
          <KpiCard icon={ClipboardList} label={t('clientTalentCheck.kpiD2.openProcesses', { defaultValue: 'Open processes' })}
                   value={kpis.open_processes} accent="primary" testid="tcheck-kpi-open" />
          <KpiCard icon={CheckCircle2} label={t('clientTalentCheck.kpiD2.closedProcesses', { defaultValue: 'Closed processes' })}
                   value={kpis.closed_processes} accent="warning" testid="tcheck-kpi-closed" />
          <KpiCard icon={Users} label={t('clientTalentCheck.kpiD2.activeCandidates', { defaultValue: 'Active candidates' })}
                   value={kpis.active_candidates} accent="info" testid="tcheck-kpi-candidates" />
          <KpiCard icon={Send} label={t('clientTalentCheck.kpiD2.pendingAssessments', { defaultValue: 'Pending assessments' })}
                   value={kpis.pending_assessments} accent="warning" testid="tcheck-kpi-pending-assessments" />
          <KpiCard icon={FileText} label={t('clientTalentCheck.kpiD2.reportsAwaitingReview', { defaultValue: 'Reports in review' })}
                   value={kpis.reports_awaiting_review} accent="info" testid="tcheck-kpi-reports-review" />
          <KpiCard icon={FileCheck} label={t('clientTalentCheck.kpiD2.reportsReleased', { defaultValue: 'Reports released' })}
                   value={kpis.reports_released} accent="success" testid="tcheck-kpi-reports-released" />
          <KpiCard icon={BadgeCheck} label={t('clientTalentCheck.kpiD2.hiringCompleted', { defaultValue: 'Hires completed' })}
                   value={kpis.hiring_completed} accent="success" testid="tcheck-kpi-hires" />
        </div>

        {/* Founder UAT 2026-06 — executive visual analytics (real data only) */}
        {(kpis.open_processes > 0 || kpis.closed_processes > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="tcheck-analytics-row">
            <DonutCard
              title={t('clientTalentCheck.analytics.processTitle', { defaultValue: 'Procesos por estado' })}
              testid="tcheck-chart-processes"
              accent={SEM.blue}
              data={[
                { name: t('clientTalentCheck.kpiD2.openProcesses', { defaultValue: 'Abiertos' }), value: kpis.open_processes || 0, color: SEM.orange },
                { name: t('clientTalentCheck.kpiD2.closedProcesses', { defaultValue: 'Cerrados' }), value: kpis.closed_processes || 0, color: SEM.green },
              ]}
              emptyText={t('clientTalentCheck.analytics.empty', { defaultValue: 'Sin procesos todavía.' })}
            />
            <ProgressCard
              title={t('clientTalentCheck.analytics.pipelineTitle', { defaultValue: 'Pipeline (situación actual, no acumulado)' })}
              testid="tcheck-chart-pipeline"
              accent={SEM.teal}
              rows={[
                { label: t('clientTalentCheck.kpiD2.pendingAssessments', { defaultValue: 'Evaluaciones pendientes' }), value: kpis.pending_assessments || 0, color: SEM.yellow },
                { label: t('clientTalentCheck.kpiD2.reportsAwaitingReview', { defaultValue: 'Informes en revisión' }), value: kpis.reports_awaiting_review || 0, color: SEM.orange },
                { label: t('clientTalentCheck.kpiD2.reportsReleased', { defaultValue: 'Informes liberados' }), value: kpis.reports_released || 0, color: SEM.blue },
                { label: t('clientTalentCheck.kpiD2.hiringCompleted', { defaultValue: 'Contrataciones completadas' }), value: kpis.hiring_completed || 0, color: SEM.green },
              ]}
              emptyText={t('clientTalentCheck.analytics.empty', { defaultValue: 'Sin procesos todavía.' })}
            />
          </div>
        )}

        {/* Primary actions row */}
        <div className="flex flex-wrap gap-2" data-testid="tcheck-actions-row">
          <Button
            size="sm"
            className="bg-gradient-primary hover:opacity-90 gap-1.5"
            onClick={() => setDialogOpen(true)}
            data-testid="tcheck-create-process"
          >
            <Send className="h-4 w-4" /> {t('clientTalentCheck.actions.requestEval')}
          </Button>
          <RequestHMSupport suite="talentcheck" origin="/client/talentcheck" />
          <Link to="/client/recruitment/candidates">
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="tcheck-external-candidates">
              <Users className="h-4 w-4" />
              {t('clientTalentCheck.actions.externalCandidates', { defaultValue: i18n.language?.startsWith('es') ? 'Candidatos externos' : 'External candidates' })}
            </Button>
          </Link>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExportPdf}
            data-testid="tcheck-export-pdf"
            className="gap-1.5"
          >
            <Download className="h-4 w-4" /> {t('clientTalentCheck.actions.exportPdf')}
          </Button>
        </div>

        {/* Founder UAT Wave 2 — pending-approval strip */}
        {pendingReqs.length > 0 && (
          <Card className="p-4 shadow-card border-warning/40 bg-warning/5" data-testid="tcheck-pending-strip">
            <div className="flex items-center gap-2 mb-2">
              <Hourglass className="h-4 w-4 text-warning" />
              <h3 className="font-semibold text-sm">
                {t('clientTalentCheck.pendingStrip.title', { defaultValue: 'Legacy requests' })}
              </h3>
              <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30 tabular-nums">
                {pendingReqs.length}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {t('clientTalentCheck.pendingStrip.subtitle', {
                defaultValue: 'Requests created under the previous approval flow. New processes are created autonomously and do not require approval.',
              })}
            </p>
            <ul className="space-y-2">
              {pendingReqs.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/70 bg-card px-3 py-2" data-testid={`tcheck-pending-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {r.candidate?.name || (r.collaborators || []).map((c) => c.name || c.email).join(', ') || '—'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {r.position || '—'} · {bizDate(r.created_at, i18n.language)}
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className={r.status === 'cancellation_requested'
                      ? 'bg-destructive/10 text-destructive border-destructive/30 text-xs'
                      : 'bg-warning/10 text-warning border-warning/30 text-xs'}
                    data-testid={`tcheck-pending-status-${r.id}`}
                  >
                    {r.status === 'cancellation_requested'
                      ? t('pages.clientRequests.statusCancellationRequested', { defaultValue: 'Cancellation requested' })
                      : t('pages.clientRequests.statusPending', { defaultValue: 'Pending' })}
                  </Badge>
                  {r.status === 'pending' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 text-destructive hover:text-destructive text-xs"
                      onClick={() => { setCancelReq(r); setCancelReason(''); }}
                      data-testid={`tcheck-pending-cancel-${r.id}`}
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      {t('pages.clientRequests.requestCancel', { defaultValue: 'Request cancellation' })}
                    </Button>
                  )}
                  {r.status === 'cancellation_requested' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                      onClick={() => confirmLegacyCancellation(r)}
                      disabled={confirmingCancel === r.id}
                      data-testid={`tcheck-pending-confirm-cancel-${r.id}`}
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      {confirmingCancel === r.id
                        ? t('common.working', { defaultValue: 'Working…' })
                        : t('clientTalentCheck.pendingStrip.confirmCancel', { defaultValue: 'Confirmar cancelación' })}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Pipeline */}
        <Card className="p-5 shadow-card" data-testid="tcheck-pipeline">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold">{t('clientTalentCheck.pipeline.title')}</h2>
              <p className="text-sm text-muted-foreground">{t('clientTalentCheck.pipeline.subtitle')}</p>
            </div>
            <button
              className="text-sm text-primary hover:underline"
              onClick={() => navigate('/client/recruitment')}
              data-testid="tcheck-pipeline-viewall"
            >
              {t('clientTalentCheck.actions.viewAllPipeline')}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {stageBuckets.map((count, idx) => {
              const Icon = STAGE_ICONS[idx];
              return (
                <div
                  key={idx}
                  className="border rounded-lg p-3 text-center hover:shadow-md transition-smooth bg-card"
                  data-testid={`tcheck-stage-${idx + 1}`}
                >
                  <Icon className="h-4 w-4 mx-auto text-primary mb-1" />
                  <p className="text-xl font-bold tabular-nums">{count}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                    {t(`clientTalentCheck.pipeline.stage${idx + 1}`)}
                  </p>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Sprint TC-D.2 — Enhanced recruitment list */}
        <Card className="p-5 shadow-card" data-testid="tcheck-recruitment-list">
          <div className="mb-4 flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold">
                {t('clientTalentCheck.recruitmentList.title', { defaultValue: 'Recruitment processes' })}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('clientTalentCheck.recruitmentList.subtitle', {
                  defaultValue: 'Each row shows the live state of one recruitment process.',
                })}
              </p>
            </div>
            <button
              className="text-sm text-primary hover:underline"
              onClick={() => navigate('/client/recruitment')}
              data-testid="tcheck-rlist-viewall"
            >
              {t('clientTalentCheck.actions.viewAllProcesses')}
            </button>
          </div>
          {procs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="tcheck-rlist-empty">
              {t('clientTalentCheck.processes.empty')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('clientTalentCheck.recruitmentList.th.position', { defaultValue: 'Position' })}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('clientTalentCheck.recruitmentList.th.department', { defaultValue: 'Department' })}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t('clientTalentCheck.recruitmentList.th.hiringManager', { defaultValue: 'Hiring manager' })}</TableHead>
                    <TableHead>{t('clientTalentCheck.recruitmentList.th.stage', { defaultValue: 'Stage' })}</TableHead>
                    <TableHead className="text-center">{t('clientTalentCheck.recruitmentList.th.candidates', { defaultValue: 'Candidates' })}</TableHead>
                    <TableHead className="text-center">{t('clientTalentCheck.recruitmentList.th.shortlisted', { defaultValue: 'Shortlist' })}</TableHead>
                    <TableHead className="text-center hidden md:table-cell">{t('clientTalentCheck.recruitmentList.th.pendingAsmt', { defaultValue: 'Pending asmt.' })}</TableHead>
                    <TableHead className="text-center hidden md:table-cell">{t('clientTalentCheck.recruitmentList.th.pendingReports', { defaultValue: 'Pending reports' })}</TableHead>
                    <TableHead>{t('clientTalentCheck.recruitmentList.th.status', { defaultValue: 'Status' })}</TableHead>
                    <TableHead className="hidden xl:table-cell">{t('clientTalentCheck.recruitmentList.th.lastActivity', { defaultValue: 'Last activity' })}</TableHead>
                    <TableHead className="text-right">{t('clientTalentCheck.recruitmentList.th.action', { defaultValue: 'Action' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {procs.map((p) => {
                    const statusTone = {
                      warming_up: 'bg-info/10 text-info border-info/20',
                      in_progress: 'bg-warning/10 text-warning border-warning/20',
                      awaiting_decision: 'bg-blue-100 text-blue-700 border-blue-200',
                      filled: 'bg-success/10 text-success border-success/20',
                      closed: 'bg-muted text-muted-foreground border-muted',
                    }[p.status_summary] || 'border-input';
                    return (
                      <TableRow
                        key={p.id}
                        className="hover:bg-muted/30 cursor-pointer"
                        onClick={() => navigate(`/client/recruitment/${p.id}`)}
                        data-testid={`tcheck-rlist-row-${p.id}`}
                      >
                        <TableCell className="font-medium">
                          {p.position_title || '—'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                          {p.department || '—'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                          {p.hiring_manager_name || '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs whitespace-nowrap">
                            {p.stage || 0}/11
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center tabular-nums">{p.candidate_count || 0}</TableCell>
                        <TableCell className="text-center tabular-nums">{p.shortlisted_count || 0}</TableCell>
                        <TableCell className="text-center tabular-nums hidden md:table-cell">{p.pending_assessments || 0}</TableCell>
                        <TableCell className="text-center tabular-nums hidden md:table-cell">{p.pending_reports || 0}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs capitalize ${statusTone}`} data-testid={`tcheck-rlist-status-${p.id}`}>
                            {t(`clientTalentCheck.recruitmentList.status.${p.status_summary}`, {
                              defaultValue: (p.status_summary || 'in_progress').replace(/_/g, ' '),
                            })}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell text-xs text-muted-foreground">
                          {p.last_activity_at ? bizDate(p.last_activity_at, i18n.language) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="h-7 gap-1.5" data-testid={`tcheck-rlist-open-${p.id}`}>
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Released reports column (kept compact under the new list) */}
        <Card className="p-5 shadow-card" data-testid="tcheck-reports-list">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold">{t('clientTalentCheck.reports.title')}</h2>
              <p className="text-xs text-muted-foreground">{t('clientTalentCheck.reports.subtitle')}</p>
            </div>
            <button
              className="text-sm text-primary hover:underline"
              onClick={() => navigate('/client/reports')}
            >
              {t('clientTalentCheck.actions.viewAllReports')}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-3" data-testid="tcheck-reports-general-note">
            {t('clientTalentCheck.reports.generalNote')}
          </p>
          {(() => {
            // TC-UAT-003 — the TalentCheck card lists candidate reports only;
            // org-level (Climara) reports live on their own page and would
            // otherwise render as nameless rows.
            const candReports = reports.filter((r) => r.report_kind !== 'climara'
              && (r.candidate?.name || r.candidate_name || r.user_name));
            return candReports.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {t('clientTalentCheck.reports.empty')}
            </p>
          ) : (() => {
            // Sprint A · G-4 — group reports by recruitment_process_id when set.
            const groups = new Map();
            const ungrouped = [];
            for (const r of candReports.slice(0, 12)) {
              if (r.recruitment_process_id) {
                const key = r.recruitment_process_id;
                if (!groups.has(key)) {
                  groups.set(key, { pid: key, title: r.process_position_title || r.position || '—', items: [] });
                }
                groups.get(key).items.push(r);
              } else {
                ungrouped.push(r);
              }
            }
            const renderRow = (r, i) => (
              <li key={r.id || i} className="flex items-start gap-3 pb-3 border-b last:border-0"
                  data-testid={`tcheck-report-${i}`}
                  data-process-id={r.recruitment_process_id || ''}>
                <FileCheck className="h-4 w-4 text-success mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {r.candidate?.name || r.candidate_name || r.user_name || '—'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{r.position || r.role || '—'}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {r.status === 'released_to_client' ? t('clientTalentCheck.reports.ready') : t('clientTalentCheck.reports.new')}
                    </Badge>
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() => navigate(`/client/reports/${r.id}`)}
                    >
                      {t('clientTalentCheck.reports.viewReport')} <ArrowRight className="inline h-3 w-3" />
                    </button>
                  </div>
                </div>
              </li>
            );
            return (
              <div className="space-y-4">
                {[...groups.values()].map((g) => (
                  <div key={g.pid} data-testid={`tcheck-report-group-${g.pid}`}>
                    <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">{g.title}</p>
                    <ul className="space-y-3">{g.items.map(renderRow)}</ul>
                  </div>
                ))}
                {ungrouped.length > 0 && (
                  <div data-testid="tcheck-report-group-ungrouped">
                    {groups.size > 0 && (
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                        {t('clientTalentCheck.reports.otherGroup', { defaultValue: 'Other' })}
                      </p>
                    )}
                    <ul className="space-y-3">{ungrouped.map(renderRow)}</ul>
                  </div>
                )}
              </div>
            );
          })();
          })()}
        </Card>

        {/* How it works */}
        <Card className="p-5 shadow-card" data-testid="tcheck-howitworks">
          <h2 className="text-lg font-semibold mb-4">{t('clientTalentCheck.howItWorks.title')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            {[1, 2, 3, 4, 5, 6, 7].map((n) => {
              const icons = [Send, FileText, Users, BadgeCheck, Search, Gavel, CheckCircle2];
              const Icon = icons[n - 1];
              return (
                <div key={n} className="border rounded-lg p-3" data-testid={`tcheck-step-${n}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-bold">
                      {n}
                    </span>
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-sm">{t(`clientTalentCheck.howItWorks.s${n}Title`)}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{t(`clientTalentCheck.howItWorks.s${n}Desc`)}</p>
                </div>
              );
            })}
          </div>
        </Card>

        <p className="text-xs text-muted-foreground italic">
          {t('clientTalentCheck.footerNote')}
        </p>
      </div>

      <ClientTalentCheckCreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={() => { setDialogOpen(false); load(); }}
      />

      {/* Founder UAT Wave 2 — cancellation dialog (audit-safe) */}
      <Dialog open={!!cancelReq} onOpenChange={(o) => { if (!o) setCancelReq(null); }}>
        <DialogContent className="max-w-md" data-testid="tcheck-cancel-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.clientRequests.cancelDialogTitle', { defaultValue: 'Request cancellation' })}</DialogTitle>
            <DialogDescription>
              {t('pages.clientRequests.cancelDialogDesc', { defaultValue: 'Your CTH advisor will review this cancellation. The request record is preserved for audit purposes.' })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t('pages.clientRequests.cancelReasonLabel', { defaultValue: 'Reason' })}</Label>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={t('pages.clientRequests.cancelReasonPh', { defaultValue: 'Why should this request be cancelled?' })}
              data-testid="tcheck-cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelReq(null)} disabled={cancelSaving}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant="destructive"
              onClick={submitCancellation}
              disabled={cancelSaving || cancelReason.trim().length < 3}
              data-testid="tcheck-cancel-submit"
            >
              {cancelSaving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {t('pages.clientRequests.cancelSubmit', { defaultValue: 'Submit cancellation request' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

